import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import {
  Box,
  Card,
  CardContent,
  SearchInput,
  Table,
  Typography
} from "@dealer-center/core-components";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import type { NrtRecord } from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { StatusChip } from "./StatusChip";

const PAGE_SIZE = 5;

export interface RecordsTableProps {
  records: NrtRecord[];
}

interface NrtMappingTableRow extends NrtRecord {
  key: string;
}

// Scoped to the fields actually shown as columns below — id/caseNumber/
// createdBy/tags exist on NrtRecord but have no visible column, so matching
// against them would filter the table with no on-screen explanation of why
// a row matched. map/notes/select are nullable, so skip null values rather
// than let String(null) turn into the literal text "null" and match every
// record with an unset field.
const SEARCHABLE_FIELDS = [
  "accountId",
  "providerId",
  "status",
  "type",
  "classification",
  "select",
  "map",
  "notes"
] as const satisfies readonly (keyof NrtRecord)[];

const matchesSearchTerm = (record: NrtRecord, term: string) =>
  SEARCHABLE_FIELDS.some(field => {
    const value = record[field];
    return value !== null && String(value).toLowerCase().includes(term);
  });

// id is only used as the row key (see rowKey="key" below), not shown as a
// column. Every visible column is sortable; sortableColumns below turns
// sorting on table-wide, and each column supplies its own string-compare
// sorter (nullable fields fall back to "" so null never breaks localeCompare).
const compareStrings = (a: string | null, b: string | null) =>
  (a ?? "").localeCompare(b ?? "");

const columns: TypeSafeTableColumns<NrtMappingTableRow> = [
  {
    dataIndex: "accountId",
    title: "Account ID",
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.accountId, b.accountId)
  },
  {
    dataIndex: "providerId",
    title: "Provider ID",
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.providerId, b.providerId)
  },
  {
    dataIndex: "status",
    title: "Status",
    render: (status: NrtRecord["status"]) => <StatusChip status={status} />,
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.status, b.status)
  },
  {
    dataIndex: "type",
    title: "Type",
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.type, b.type)
  },
  {
    dataIndex: "classification",
    title: "Classification",
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.classification, b.classification)
  },
  {
    dataIndex: "select",
    title: "Select",
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.select, b.select)
  },
  {
    dataIndex: "map",
    title: "Map",
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.map, b.map)
  },
  {
    dataIndex: "notes",
    title: "Notes",
    sorter: (a: NrtMappingTableRow, b: NrtMappingTableRow) =>
      compareStrings(a.notes, b.notes)
  }
];

export const RecordsTable = ({ records }: RecordsTableProps) => {
  const [searchValue, setSearchValue] = useState("");

  const filteredRecords = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      return records;
    }
    return records.filter(record => matchesSearchTerm(record, term));
  }, [records, searchValue]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  return (
    <Card
      content={
        <CardContent>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 2
            }}
          >
            <Typography sx={{ fontWeight: "bold" }} variant="h4">
              Mapping Records ({filteredRecords.length})
            </Typography>
            <SearchInput
              data-testid="nrt-mapping-tool-search-input"
              inputValue={searchValue}
              placeholder="Search records..."
              sx={{ maxWidth: "300px" }}
              onInputChange={handleSearchChange}
            />
          </Box>
          <Table.Table
            sortableColumns
            columns={columns}
            currentPageSize={PAGE_SIZE}
            data={filteredRecords.map(record => ({
              ...record,
              key: String(record.id)
            }))}
            dataDensity="small"
            emptyText="No mapping records found"
            rowKey="key"
          />
        </CardContent>
      }
    />
  );
};
